import React, { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import styles from "./MembershipPage.module.css";
import Header from "../components/Header";
import MembershipItem from "../components/MembershipItem";
import BillingItem from "../components/BillingItem";
import ResourceList from "../components/ResourceList";
import Button from "../components/Button";
import Modal from "../components/Modal";
import CardModal from "../components/CardModal";
import Loader from "components/Loader";
import TermsAndConditions from "components/TermsAndConditions";
import moment from "moment";
import "moment-timezone";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMinusCircle,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import {
  faCcVisa,
  faCcMastercard,
  faCcDiscover,
  faCcDinersClub,
  faCcJcb,
} from "@fortawesome/free-brands-svg-icons";
import {
  useMembership,
  useProcessPost,
  useResources,
} from "./useMembershipPage";
import { useCreditCards } from "CreditCards/useCreditCards";
import { useAccount } from "utils/AccountInfoContext";
import mobile from "mobileBridge";
import environment from "environment";
import { NavigationSvc } from "../navigationScv";
import premierIcon from "../assets/Premier_Membership_Icon.svg";
import premierIcon2 from "../assets/Premier_Membership_Icon_2.svg";
import checkMark from "../assets/Check_Mark.svg";
import cardCheck from "../assets/cardCheckMark.svg";
import Checkbox from "components/Checkbox";
import MembershipAddressItem from "components/MembershipAddressItem";
import WarningPrimaryModal from "components/WarningPrimaryModal";
import digitalDeliveryIcon from "../assets/Digital_Delivery.svg";
import dreamStreamLogoBackground from "../assets/DreamStreamLogoBackground.svg";
import dreamStreamLogoSymbol from "../assets/DreamStreamLogoSymbol.svg";
import topicsLogoBackground from "../assets/TopicsLogoBackground.svg";
import topicsLogoSymbol from "../assets/TopicsLogoSymbol.svg";
import infoIcon from "../assets/Information_Icon.svg";
import PremierLight from "../assets/PremierLight.svg";
import PremierDark from "../assets/PremierIconDark.svg";
import InfoCloseBtn from "../assets/InfoModalCloseButton.svg";
import DigitalDeliveryDark from "../assets/Digital_DeliveryDark.svg";
import topicsLogoBackgroundDark from "../assets/TopicsIconDarkBackground.svg";
import DreamStreamDark from "../assets/DreamStreamDark.svg";
import AlertIcon from "../assets/alert_icon.svg";
import { useTranslation } from "react-i18next";

const WEB_APP_COUNTRY = environment?.WEB_APP_COUNTRY;
const isANZ = WEB_APP_COUNTRY === "AU";

const cardTypeToIcon = {
  visa: faCcVisa,
  mastercard: faCcMastercard,
  discover: faCcDiscover,
  "discover card": faCcDiscover,
  diners: faCcDinersClub,
  "diners club": faCcDinersClub,
  jcb: faCcJcb,
};

function getCardIcon(cardType) {
  if (!cardType) return null;
  const formattedCardType = cardType.toLowerCase().replace(/\s+/g, "");
  const iconKey = Object.keys(cardTypeToIcon).find((key) =>
    formattedCardType.includes(key.replace(/\s+/g, ""))
  );
  return iconKey ? cardTypeToIcon[iconKey] : null;
}

const getMembershipIcon = (type) => {
  if (
    (type && type.includes("DreamStream")) ||
    (type && type.includes("Digital Delivery"))
  ) {
    return premierIcon;
  } else if (type === "Premier Membership") {
    return premierIcon2;
  }
  return premierIcon2; // Default icon
};

const formatDateToOne = (dateString) => {
  const date = moment.utc(dateString);
  return date.format("M.D.YYYY");
};

function formatIBODate(dateString) {
  const dateObj = new Date(dateString);
  const formattedDate = moment(dateObj).format("M.D.YYYY");

  const minusOneDay = moment(dateObj).subtract(1, "days").format("M.D.YYYY");
  const minusOneYearAndDay = moment(dateObj)
    .subtract(1, "days")
    .subtract(1, "years")
    .format("M.D.YYYY");

  return {
    nextBilling: formattedDate,
    expires: minusOneDay,
    began: minusOneYearAndDay,
  };
}

const getNextBillingDate = () => {
  let nextMonth = moment().add(1, "months").startOf("month");
  let businessDaysCount = 0;
  while (businessDaysCount < 2) {
    if (nextMonth.isoWeekday() < 6) {
      businessDaysCount++;
    }
    if (businessDaysCount < 2) {
      nextMonth.add(1, "day");
    }
  }
  return nextMonth.format("MM.DD.YYYY");
};

const getEffectiveDate = (requiresDowngrade) => {
  return requiresDowngrade
    ? getNextBillingDate()
    : moment().format("MM.DD.YYYY");
};

const formatDateToTwo = (dateString) => {
  const date = moment.utc(dateString);
  if (date.isBefore(moment(), "day")) {
    return getNextBillingDate();
  }
  return date.format("MM.DD.YYYY");
};

export function MembershipPage() {
  const history = useHistory();
  const location = useLocation();
  const navigationSvc = new NavigationSvc();
  const [prefetch, setPrefetch] = useState(null);
  const {
    data,
    isError,
    isLoading,
    refetch,
    scheduleChange,
    cancelScheduledChange,
    isRefetching,
  } = useMembership(prefetch);

  const {
    privacyData, // terms and conditions stuff
    tcAgreePost,
    billingData,
    accountData,
    consentData, // amway stuff
    cachedIBOInfo,
    refetchIssuesData,
  } = useAccount();

  const { processPost } = useProcessPost();
  const { data: resourcesData } = useResources();

  const {
    data: cardData,
    isError: isCardError,
    isLoading: isCardLoading,
    updateCard,
    addCustomer,
    addCard,
  } = useCreditCards();

  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [hasCurrentMembership, setHasCurrentMembership] = useState(false);
  const [showRenewMessage, setShowRenewMessage] = useState(false);
  const [showBillingAndResources, setShowBillingAndResources] = useState(true);
  const [selected, setSelected] = useState(false);
  const [showSelectHeader, setShowSelectHeader] = useState(false);
  const [chevronClicked, setChevronClicked] = useState(false);
  const [showConfirmationBox, setShowConfirmationBox] = useState(false);
  const [showProcessConfirmationBox, setShowProcessConfirmationBox] =
    useState(false);
  const [hasAddress, setHasAddress] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState("NA");
  const [showTOSModal, setShowTOSModal] = useState(false);
  const [showAddNewCardButton, setShowAddNewCardButton] = useState(false);
  const [showMembershipAndResources, setShowMembershipAndResources] =
    useState(true);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [showNoticeModal, setShowNoticeModal] = useState(false);
  const [isCardModalOpen, setIsCardModalOpen] = useState(false);
  const [headerText, setHeaderText] = useState("Settings");
  const [currentMembership, setCurrentMembership] = useState([]);
  const [membershipDates, setMembershipDates] = useState({});
  const [availableMemberships, setAvailableMemberships] = useState([]);
  const [availableMembershipSelected, setAvailableMembershipSelected] =
    useState(null);
  const [scheduledMembership, setScheduledMembership] = useState({});
  const [cardList, setCardList] = useState([]);
  const [billingInfo, setBillingInfo] = useState({});
  const [paymentToken, setPaymentToken] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [nickName, setNickName] = useState("");
  const [triggerCollectClick, setTriggerCollectClick] = useState(false);
  const [currentCard, setCurrentCard] = useState(null);
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [selectedProratedAmount, setSelectedProratedAmount] = useState("");
  const [showReceipt, setShowReceipt] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const [effectiveDate, setEffectiveDate] = useState(getEffectiveDate(false));
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showPremierWarningModal, setShowPremierWarningModal] = useState(false);
  const [showAddCardWarning, setShowAddCardWarning] = useState(false);
  const [showReviewOrder, setShowReviewOrder] = useState(false);
  const [isFreeTrial, setIsFreeTrial] = useState(false);
  const [compIBONextBillDate, setCompIBONextBillDate] = useState("");
  const [isPremier, setIsPremier] = useState(false);
  const [hasDreamStream, setHasDreamStream] = useState(false);
  const [hasDigitalDelivery, setHasDigitalDelivery] = useState(false);
  const [displayPremier, setDisplayPremier] = useState(false);
  const [displayDreamStream, setDisplayDreamStream] = useState(false);
  const [displayDigitalDelivery, setDisplayDigitalDelivery] = useState(false);
  const [initialStates, setInitialStates] = useState({});
  const [changedStates, setChangedStates] = useState({});
  const [showNewMembershipSelection, setShowNewMembershipSelection] =
    useState(false);
  const [isRefetch, setIsRefetch] = useState(false);

  const subscriptionTypes = {
    Cancellation: 1,
    Basic: 2,
    Premier: 4,
    DigitalDelivery: 8,
    DreamStream: 65536,
  };

  useEffect(() => {
    setLoading(isLoading || isCardLoading);
  }, [isLoading, isCardLoading]);

  useEffect(() => {
    if (cachedIBOInfo) {
      setIsFreeTrial(cachedIBOInfo.IsFreeTrial);
      setCompIBONextBillDate(cachedIBOInfo.NextBillDate);
    }
  }, [cachedIBOInfo]);

  useEffect(() => {
    if (data && !isRefetch) {
      const formattedDate = formatDateToTwo(data.NextBilledOn);
      setCurrentMembership({
        type: data.current?.title,
        price: data.current?.amount,
        nextBilling: formattedDate,
        endsOn: moment.utc(data.scheduled?.effectiveOn).format("M.D.YYYY"),
      });

      setMembershipDates({
        nextBilledOn: formattedDate,
        nextPeriodStartsOn: moment
          .utc(data.NextPeriodStartsOn)
          .format("M.D.YYYY"),
      });

      const membershipTypeValue = data?.current?.type;

      const isPremier = (membershipTypeValue & subscriptionTypes.Premier) !== 0;
      const hasDreamStream =
        (membershipTypeValue & subscriptionTypes.DreamStream) !== 0;
      const hasDigitalDelivery =
        (membershipTypeValue & subscriptionTypes.DigitalDelivery) !== 0;

      setIsPremier(isPremier);
      setHasDreamStream(hasDreamStream);
      setHasDigitalDelivery(hasDigitalDelivery);
      setInitialStates({
        dreamStream: hasDreamStream,
        digitalDelivery: hasDigitalDelivery,
        premier: isPremier,
      });
      setChangedStates({
        dreamStream: hasDreamStream,
        digitalDelivery: hasDigitalDelivery,
        premier: isPremier,
      });

      const available = data.available;

      const subscriptionOptions = new Set();

      available.forEach((item) => {
        Object.values(subscriptionTypes).forEach((subType) => {
          if (item.type & subType) {
            subscriptionOptions.add(subType);
          }
        });
      });

      setDisplayPremier(subscriptionOptions.has(subscriptionTypes.Premier));
      setDisplayDreamStream(
        subscriptionOptions.has(subscriptionTypes.DreamStream)
      );
      setDisplayDigitalDelivery(
        subscriptionOptions.has(subscriptionTypes.DigitalDelivery)
      );

      setAvailableMemberships(data.available);
      setScheduledMembership(data.scheduled || {});
      setHasCurrentMembership(!!data.current);
      setShowRenewMessage(!data.current);
      setHasAddress(data.hasAddress);
    }
  }, [data, consentData]);

  useEffect(() => {
    if (data && !isRefetching) {
      setAvailableMemberships(data.available);
      setIsRefetch(false);
    }
  }, [isRefetching]);

  useEffect(() => {
    if (!hasCurrentMembership) {
      setShowRenewMessage(true);
      if (headerText === "Membership") {
        setShowNewMembershipSelection(true);
      }
    }
    if (hasCurrentMembership) {
      setShowNewMembershipSelection(false);
    }
  }, [location.pathname, hasCurrentMembership]);

  useEffect(() => {
    if (!cardData || !cardData.cards || cardData.cards.length === 0) {
      // No cardData or cards, clear cardList and billingInfo
      setCardList([]);
      setBillingInfo(null);
      return;
    }

    if (cardData) {
      let cardArray = [];
      let primaryCardFound = false;

      cardData.cards.forEach((card) => {
        const paddedMonth = card.expireMonth.padStart(2, "0");
        cardArray.push({
          selected: card.isPrimary,
          cardIcon: getCardIcon(card.cardType),
          lastFour: card.lastFour,
          expiration: `${paddedMonth}/${card.expireYear.slice(2)}`,
          name: card.nickname,
          id: card.membershipCreditCardId,
          zip: card.zip,
          cardType: card.cardType,
        });

        if (card.isPrimary) {
          setBillingInfo({
            type: card.cardType,
            lastFour: card.lastFour,
            zip: card.zip,
            id: card.membershipCreditCardId,
          });
          primaryCardFound = true;
        }
      });

      setCardList(cardArray);

      if (!primaryCardFound) {
        setBillingInfo(null);
      }
    }
  }, [cardData]);

  const isBillingDay = () => {
    const today = moment().startOf("day");
    const billingDay = moment(data.NextBilledOn).startOf("day");
    return today.isSame(billingDay);
  };

  const calculateProratedAmount = (membership) => {
    if (consentData?.payment_eligible === "No") {
      return 0;
    }
    if (isBillingDay()) {
      return membership.amount;
    } else {
      return membership.proratedAmount;
    }
  };

  const handleCardSelection = async (index) => {
    const selectedCard = cardList[index];
    const updatedCardList = cardList.map((card, i) => ({
      ...card,
      selected: index === i,
    }));
    setCardList(updatedCardList);

    const updatedCardData = {
      cardHolder: selectedCard.name,
      expireMonth: selectedCard.expiration.split("/")[0],
      expireYear: selectedCard.expiration.split("/")[1],
      nickname: selectedCard.name,
      zip: selectedCard.zip,
      membershipCreditCardId: selectedCard.id,
      isPrimary: true,
    };
    try {
      await updateCard.mutateAsync(updatedCardData);

      // Show success message after resetting the state
      resetMembershipPage();
      setShowSuccessMessage(true);

      setTimeout(() => {
        setShowSuccessMessage(false);
        history.push("/membership");
      }, 3000);
    } catch (error) {
      console.error(t("Error updating card"), error);
    }
  };

  const handleRemove = () => setShowRemoveModal(true);

  //Handles to confirm membership cancellation
  const handleRemoveConfirm = async () => {
    // setLoading(true);
    try {
      await cancelScheduledChange.mutateAsync();
    } catch (error) {
      console.error(t("Error cancelling scheduled changes"), error);
    }
    await refetch();
    setShowRemoveModal(false);
  };
  const handleRemoveCancel = () => setShowRemoveModal(false);
  const handleCloseNoticeModal = () => setShowNoticeModal(false);

  //Triggers the Cancel button in the footer
  const cancelMembership = () => {
    setShowConfirmationBox(false);
    setShowProcessConfirmationBox(false);
    setSelected(false);

    if (showReviewOrder) {
      setShowReviewOrder(false);
      setShowAddCardWarning(false);
      setShowConfirmationBox(false);
      setSelected(false);
      setShowSelectHeader(true);
      history.push("/membership");
    }
  };

  //Triggers confirm button in footer
  const confirmOnClick = () => {
    if (!privacyData.agreedLatest) {
      setShowTOSModal(true);
    } else {
      handleTaCConfirm();
    }
  };

  const handleProcessClick = async () => {
    // If there's no billing info, show the review order
    if (!billingInfo) {
      setShowTOSModal(false);
      setShowReviewOrder(true);
      setShowSelectHeader(false);
      return;
    }
    setLoading(true);

    const selectedMembership =
      availableMemberships[availableMembershipSelected];

    const { price, tax } = calculateFooterPriceAndTax(selectedMembership);

    const totalAmount =
      String(price).toLowerCase() == "free"
        ? "Free"
        : (parseFloat(price) + parseFloat(tax)).toFixed(2);

    const membershipData = {
      ibo: Number(accountData.account.ibo),
      membershipCreditCardId: billingInfo?.id,
      type: selectedMembership.type,
      guid: selectedMembership.guid,
      amount: totalAmount, // Ensure this is included in the payload
    };

    // alert(JSON.stringify({ ...membershipData, ...selectedMembership }));
    // return;
    try {
      if (selectedMembership.requiresDowngrade && !showNewMembershipSelection) {
        await scheduleChange.mutateAsync(selectedMembership.type).catch((e) => {
          alert(e);
        });

        // also upgrade if premier + topics -> premier + dd
        // or premier + dd -> premier + topics
        if (
          (data?.current?.type == 65540 && selectedMembership.type == 12) ||
          (data?.current?.type == 12 && selectedMembership.type == 65540)
        ) {
          let showReceipt = true;
          await processPost.mutateAsync(membershipData).catch((e) => {
            showReceipt = false;
            alert(e);
          });

          // if error, do not show receipt
          if (showReceipt) {
            if (!showNewMembershipSelection) {
              refetchIssuesData();
            }
            setReceiptData({
              membership: selectedMembership.title,
              amountPaid: totalAmount,
              cardType: billingInfo.type,
              cardLastFour: billingInfo.lastFour,
            });
            setShowReceipt(true);
          }
        }
      } else {
        let showReceipt = true;
        await processPost.mutateAsync(membershipData).catch((e) => {
          showReceipt = false;
          alert(e);
        });

        // if error, do not show receipt
        if (showReceipt) {
          if (!showNewMembershipSelection) {
            refetchIssuesData();
          }
          setReceiptData({
            membership: selectedMembership.title,
            amountPaid: totalAmount,
            cardType: billingInfo.type,
            cardLastFour: billingInfo.lastFour,
          });
          setShowReceipt(true);
        }
      }
      refetch();

      if (!privacyData.agreedLatest) {
        await tcAgreePost.mutateAsync();
      }
    } catch (error) {
      setLoading(false);
      console.error(t("Error scheduling changes"), error);
    }
    setShowTOSModal(false);
    setShowConfirmationBox(false);
    setShowProcessConfirmationBox(false);
    setLoading(false);
  };

  //Triggers yes button in T&C
  const handleTaCConfirm = async () => {
    setShowTOSModal(false);
    if (!isPremier) {
      if (!billingInfo) {
        handleProcessClick();
      } else {
        setShowConfirmationBox(false);
        setShowReviewOrder(true);
        setShowProcessConfirmationBox(true);
      }
    } else {
      handleProcessClick();
    }
  };

  const handleTaCCancel = () => setShowTOSModal(false);

  //Handles to confirm premier membership cancellation
  const confirmCancelMembershipModal = async () => {
    // setLoading(true);
    try {
      await scheduleChange.mutateAsync(1);
    } catch (error) {
      console.error(t("Error scheduling changes"), error);
    } finally {
      // setLoading(false);
      refetch();
      setShowCancelModal(false);
    }
  };

  const cancelMembershipModal = () => setShowCancelModal(false);

  const [showConsentIssue, setShowConsentIssue] = useState(false);
  const [showConsentGivenModal, setShowConsentGivenModal] = useState(false);

  const handleClickMembershipItem = () => {
    if (consentData?.status?.toLowerCase() != "active" && !isANZ) {
      setShowConsentIssue(true);
      return;
    }

    if (consentData?.consent_provided?.toLowerCase() != "yes" && !isANZ) {
      setShowConsentGivenModal(true);
      return;
    }

    if (!hasCurrentMembership) {
      setShowNewMembershipSelection(true);
    } else {
      setShowMembershipAndResources(true);
    }
    setShowBillingAndResources(false);
    setShowAddNewCardButton(false);
    setChevronClicked(true);
    setShowSelectHeader(true);
    setHeaderText("Membership");
    setShowRenewMessage(false);
  };

  const agreeToAmwayOnClick = () => {
    history.push("/consent");
  };

  const handleClickBillingItem = () => {
    setShowMembershipAndResources(false);
    setShowAddNewCardButton(true);
    setShowBillingAndResources(true);
    setChevronClicked(false);
    setHeaderText("Membership");
  };

  const handleHeaderClick = () => {
    if (headerText === "Membership") {
      history.push("/membership");
      resetMembershipPage();
    } else if (headerText === "Settings") {
      history.push("/");
    } else {
      history.goBack();
    }
  };

  const resetMembershipPage = () => {
    setShowReviewOrder(false);
    setChangedStates(initialStates);
    setShowNewMembershipSelection(false);
    setShowBillingAndResources(true);
    setSelected(false);
    setChevronClicked(false);
    setShowConfirmationBox(false);
    setShowCancelModal(false);
    setShowAddNewCardButton(false);
    setShowMembershipAndResources(true);
    setShowSelectHeader(false);
    setShowReceipt(false);
    setShowProcessConfirmationBox(false);
    setHeaderText("Settings");
    if (!hasCurrentMembership) {
      setShowRenewMessage(true);
    }
  };

  useEffect(() => {
    if (location.pathname === "/") {
      resetMembershipPage();
    }
  }, [location.pathname]);

  useEffect(() => {
    if (paymentToken) {
      handleAdd();
    }
  }, [paymentToken, postalCode]);

  //refetch issues data after reload
  useEffect(() => {
    const shouldRefetch = localStorage.getItem("shouldRefetch") === "true";

    if (shouldRefetch) {
      refetchIssuesData();
      localStorage.removeItem("shouldRefetch");
    }
  }, []);

  const handleAdd = async (cardInfo) => {
    // Set initial state without loading
    setCurrentCard(cardInfo);
    setPostalCode(cardInfo?.zipCode);
    setNickName(cardInfo?.nickName);

    // Trigger Collect JS
    setTriggerCollectClick((prev) => !prev);

    // Wait for the token to be set by handleTokenReceived
    await new Promise((resolve) => {
      const checkToken = () => {
        if (paymentToken) {
          resolve();
        } else {
          setTimeout(checkToken, 100);
        }
      };
      checkToken();
    });

    // Start loading only after Collect JS has successfully processed
    if (paymentToken && postalCode) {
      setIsModalLoading(true);
    } else {
      return;
    }

    try {
      // Call addCustomer and handle the response
      const customerData = await addCustomer.mutateAsync({
        paymentToken,
        postalCode,
      });

      // Handle expiration date formatting
      let expMonth, expYear, expiration;
      if (customerData.cc_exp.length === 3) {
        expMonth = `0${customerData.cc_exp.slice(0, 1)}`;
        expYear = customerData.cc_exp.slice(1);
        expiration = `${expMonth}${expYear}`;
      } else if (customerData.cc_exp.length === 4) {
        expMonth = customerData.cc_exp.slice(0, 2);
        expYear = customerData.cc_exp.slice(2);
        expiration = `${expMonth}${expYear}`;
      } else {
        console.error(
          t("Unexpected expiration date format"),
          customerData.cc_exp
        );
        expiration = customerData.cc_exp; // Use raw data in case of unexpected format
      }

      const newCardData = {
        cardHolder: `${customerData.first_name} ${customerData.last_name}`,
        expiration: expiration,
        firstSix: customerData.cc_bin,
        isPrimary: false,
        lastFour: customerData.lastFour,
        nickname: nickName,
        postalCode: postalCode,
        token: customerData.customer_vault_id,
        cardType: customerData.cardType || "credit-card",
      };

      // API call to add card
      await addCard.mutateAsync(newCardData);
      setIsCardModalOpen(false);
      // setShowAddCardWarning(false);
      setShowMembershipAndResources(false);
      setShowAddNewCardButton(true);
      setShowBillingAndResources(true);
      setChevronClicked(false);
      setShowConfirmationBox(false);
      setShowReceipt(false);
      setShowSelectHeader(false);

      // Set a flag in localStorage to trigger refetch on reload
      localStorage.setItem("shouldRefetch", "true");

      window.location.reload();
    } catch (error) {
      console.error(t("Error during the card addition"), error);
    } finally {
      // Stop loading state
      setIsModalLoading(false);
    }
  };

  const handleTokenReceived = (response) => {
    const { token, card } = response;
    setPaymentToken(token);
    setCurrentCard({
      lastFour: card.number.slice(-4),
      expMonth: card.exp.slice(0, 2),
      expYear: `20${card.exp.slice(2)}`,
      cardType: card.type,
      token: token,
    });
  };

  const calculateFooterPriceAndTax = (membership) => {
    let price = "Free";
    let tax = 0;

    if (consentData?.payment_eligible !== "No") {
      if (isBillingDay()) {
        price = membership.amount.toFixed(2);
        tax = parseFloat(membership.totalTax); // Use only memberTax
      } else {
        price = membership.proratedAmount.toFixed(2);
        tax = parseFloat(membership.proratedTotalTax); // Use only proratedMemberTax
      }
    }

    return {
      price,
      tax: tax.toFixed(2),
    };
  };

  const openCardModal = () => {
    setIsCardModalOpen(true);
  };

  const cancelOnClick = () => {
    if (scheduledMembership.title) {
      setShowNoticeModal(true);
    } else {
      setShowCancelModal(true);
    }
  };

  const getStatusMessage = (status) => {
    switch (status) {
      case 6:
        return t("Your membership is expired");
      case 8:
        return t("Your membership is cancelled");
      default:
        return "";
    }
  };

  const statusMessage = getStatusMessage(data?.status);

  const handleAddBillingCard = () => {
    setShowReviewOrder(false);
    setShowAddCardWarning(false);
    setShowMembershipAndResources(false);
    setShowAddNewCardButton(true);
    setShowBillingAndResources(true);
    setChevronClicked(false);
    setShowConfirmationBox(false);
    setShowReceipt(false);
    setShowSelectHeader(false);

    // history.push("/credit-cards");
  };

  const handleAddAddressCard = () => {
    history.push("/addresses");
  };

  const handleCancelBillingWarning = () => {
    setShowAddCardWarning(false);
    setShowConfirmationBox(false);
    setSelected(false);
    history.push("/membership");
  };

  const handleSelect = async () => {
    let selectedMembership = null;
    let selectedType = 0; // Initialize a variable to hold the bits

    // Find the available option that matches the current membership title
    const currentOption = availableMemberships.find(
      (option) => option.title === currentMembership.type
    );

    // If a matching option is found, set selectedType to that option's type
    if (currentOption) {
      selectedType = currentOption.type;
    }

    // Subscription types as defined previously
    const subscriptionTypes = {
      Cancellation: 1,
      Basic: 2,
      Premier: 4,
      DigitalDelivery: 8,
      DreamStream: 65536,
    };

    // Add or remove the Digital Delivery bit
    if (
      changedStates.digitalDelivery === true &&
      initialStates.digitalDelivery === false
    ) {
      selectedType |= subscriptionTypes.DigitalDelivery; // Add the Digital Delivery bit
    } else if (
      changedStates.digitalDelivery === false &&
      initialStates.digitalDelivery === true
    ) {
      selectedType &= ~subscriptionTypes.DigitalDelivery; // Remove the Digital Delivery bit
    }

    // Add or remove the DreamStream bit
    if (
      changedStates.dreamStream === true &&
      initialStates.dreamStream === false
    ) {
      selectedType |= subscriptionTypes.DreamStream; // Add the DreamStream bit
    } else if (
      changedStates.dreamStream === false &&
      initialStates.dreamStream === true
    ) {
      selectedType &= ~subscriptionTypes.DreamStream; // Remove the DreamStream bit
    }

    // Add or remove the Premier bit
    if (changedStates.premier === true && initialStates.premier === false) {
      selectedType |= subscriptionTypes.Premier; // Add the Premier bit
    } else if (
      changedStates.premier === false &&
      initialStates.premier === true
    ) {
      selectedType &= ~subscriptionTypes.Premier; // Remove the Premier bit
    }

    // Find the membership based on the selected type
    selectedMembership = availableMemberships.find(
      (membership) => membership.type === selectedType
    );

    // If no exact match is found, fallback to the current membership
    // if (!selectedMembership) {
    //   selectedMembership = data.current;
    // }

    // Find the index of the selectedMembership
    const selectedIndex = availableMemberships.findIndex(
      (membership) => membership.type === selectedType
    );

    // Set the selected membership index and other states
    setAvailableMembershipSelected(selectedIndex);
    setSelected(true);
    setShowConfirmationBox(true);

    const proratedAmount = calculateProratedAmount(selectedMembership);
    setSelectedProratedAmount(proratedAmount);

    const effectiveDate = getEffectiveDate(
      selectedMembership.requiresDowngrade
    );
    setEffectiveDate(effectiveDate);
  };

  // // debug
  // useEffect(() => {
  //   console.log("Changed States:", changedStates);
  //   console.log("Initial States:", initialStates);
  // }, [changedStates, initialStates]);

  const handleCheckBoxChange = async (from) => {
    if (from == "DD") {
      setChangedStates({
        digitalDelivery: !changedStates.digitalDelivery,
        dreamStream: changedStates.dreamStream,
        premier: true,
      });

      if (changedStates.digitalDelivery) {
        setPrefetch(subscriptionTypes.Premier);
        setIsRefetch(true);
      } else {
        setPrefetch(null);
        setIsRefetch(true);
      }
    } else if (from == "DS") {
      setChangedStates({
        digitalDelivery: changedStates.digitalDelivery,
        dreamStream: !changedStates.dreamStream,
        premier: true,
      });
    } else if (
      !changedStates.premier == false &&
      (changedStates.digitalDelivery == true ||
        changedStates.dreamStream == true)
    ) {
      setShowPremierWarningModal(true);
      // setChangedStates({
      //   digitalDelivery: false,
      //   dreamStream: false,
      //   premier: false,
      // });
    } else {
      setChangedStates({
        digitalDelivery: changedStates.digitalDelivery,
        dreamStream: changedStates.dreamStream,
        premier: !changedStates.premier,
      });
    }
  };

  useEffect(() => {
    if (showConfirmationBox) {
      if (
        changedStates.digitalDelivery == false &&
        changedStates.dreamStream == false &&
        changedStates.premier == false
      ) {
        // cancelMembership();
        // setShowPremierWarningModal();
      } else {
        handleSelect();
      }
      if (
        changedStates.digitalDelivery == initialStates.digitalDelivery &&
        changedStates.dreamStream == initialStates.dreamStream &&
        changedStates.premier == initialStates.premier
      ) {
        cancelMembership();
      }
    }
  }, [changedStates]);

  const handleSelectMembership = (index) => {
    const selectedMembership = availableMemberships[index];
    const proratedAmount = calculateProratedAmount(selectedMembership);
    setAvailableMembershipSelected(index);
    setSelected(true);
    setShowConfirmationBox(true);
    setSelectedProratedAmount(proratedAmount);
    setEffectiveDate(getEffectiveDate(selectedMembership.requiresDowngrade));
  };

  //Check card expiration, disable if expired
  const checkCardExpiration = (expiration) => {
    if (!expiration || typeof expiration !== "string") {
      return false;
    }

    const parts = expiration?.split("/");
    if (parts.length !== 2) {
      return false;
    }

    const [expireMonth, expireYear] = expiration?.split("/").map(Number);
    if (isNaN(expireMonth) || isNaN(expireYear)) {
      return false;
    }

    const currentDate = new Date();
    const currentMonth = currentDate.getMonth() + 1;
    const currentYear = currentDate.getFullYear() % 100;

    if (expireYear > currentYear) {
      return true;
    } else if (expireYear === currentYear) {
      return expireMonth >= currentMonth;
    } else {
      return false;
    }
  };

  return (
    <div className={styles.container}>
      {/* Header Component */}
      <Header header={t(headerText)} onClick={handleHeaderClick} />

      {headerText === "Settings" && (
        <div
          className={styles.premierContainer}
          onClick={() => {
            var appName = "signup";

            var env = (environment.environmentApp || "").toLowerCase().trim();
            if (env || env.length > 0) {
              appName += "-" + env;
            }
            mobile.isApp()
              ? navigationSvc.goToApp(appName, {})
              : window.open(environment.signUp, "_blank");
          }}
        >
          <img
            src={premierIcon}
            alt="Membership Icon"
            className={styles.premierBtnIcon}
          />

          <p className={styles.textInvite}>{t("Invite New Premier Members")}</p>
        </div>
      )}

      {/* Main Content */}
      {loading ? (
        <div>
          <Loader color={"#415364"} size={85} />
        </div>
      ) : (
        <div className={styles.content}>
          {showConsentIssue && (
            <div className={styles.consentModalOverlay}>
              <div className={styles.consentModal}>
                <img
                  src={AlertIcon}
                  alt="alert icon"
                  className={styles.alertIcon}
                />
                <div className={styles.consentModalHeader}>
                  {t("There is something wrong with account")}
                </div>
                <div className={styles.consentModalText}>
                  {t("Please visit")}{" "}
                  <span
                    onClick={() => {
                      mobile.isApp()
                        ? mobile.openLink("https://www.amway.com/")
                        : window.open("https://www.amway.com/", "_blank");
                    }}
                  >
                    www.amway.com
                  </span>{" "}
                  {t("to resolve issue before renew")}
                </div>
                <div className={styles.buttonContainer}>
                  <Button
                    buttonText={t("Close")}
                    onClick={() => {
                      setShowConsentIssue(false);
                    }}
                    className={styles.consentIssueCloseBtn}
                  />
                </div>
              </div>
            </div>
          )}

          {showConsentGivenModal && (
            <div className={styles.consentModalOverlay}>
              <div className={styles.consentModal}>
                <img
                  src={AlertIcon}
                  alt="alert icon"
                  className={styles.alertIcon}
                />
                <div className={styles.consentModalHeader}>
                  {t("Missing AP Consent")}
                </div>
                <div className={styles.consentModalText}>
                  {t("We see that you have not given")}{" "}
                  <b>{t("Approved Provider Consent")}</b> {"with Amway"}
                </div>
                <div className={styles.consentModalText}>
                  {t("Before you make any changes or renew")}
                </div>
                <div className={styles.consentModalText}>
                  {t("To provide your consent, click here")}{" "}
                  <u
                    onClick={() => {
                      mobile.isApp()
                        ? mobile.openLink(
                            "https://account.amway.com/my-account/approved-provider/WWG"
                          )
                        : window
                            .open(
                              "https://account.amway.com/my-account/approved-provider/WWG"
                            )
                            ?.focus();
                    }}
                    className={styles.hover}
                  >
                    {t("Amway Approved Provider Consent")}
                  </u>
                </div>
                <div className={styles.consentModalText}>
                  {t("After giving consent")}
                </div>
                <div className={styles.buttonContainer}>
                  <Button
                    buttonText={t("Close")}
                    onClick={() => {
                      setShowConsentGivenModal(false);
                    }}
                    className={styles.consentCloseBtn}
                  />
                </div>
              </div>
            </div>
          )}
          {showReceipt ? (
            <div className={styles.receiptContainer}>
              <h1 className={styles.selectedHeader}>
                {t("Membership Updated")}
              </h1>
              <div className={styles.currentMembership}>
                <div className={styles.headerReceipt}>
                  {t("Current Membership")}
                </div>
                <MembershipItem
                  info={currentMembership}
                  hideChevron={true}
                  icon={getMembershipIcon(currentMembership.type)}
                  hasDigitalDelivery={hasDigitalDelivery}
                  hasDreamStream={hasDreamStream}
                  isPremier={isPremier}
                  isFreeTrial={isFreeTrial}
                  compIBOInfo={formatIBODate(compIBONextBillDate)}
                />
                <br />
                {scheduledMembership.title === undefined ? (
                  <>
                    <div className={styles.topContainerDivider} />
                  </>
                ) : (
                  <div className={styles.scheduledChangesDivider}>
                    {t("Scheduled Changes")}
                  </div>
                )}
              </div>
              {/* Scheduled Changes Section */}
              {scheduledMembership.title !== undefined &&
                !showAddNewCardButton && (
                  <>
                    <div
                      className={
                        headerText === "Settings"
                          ? styles.scheduledChangesContainer
                          : styles.scheduledChangesContainerInside
                      }
                      style={{
                        cursor:
                          headerText === "Settings" ? "pointer" : "default",
                      }}
                      onClick={() => handleClickMembershipItem()}
                    >
                      <div className={styles.spacer}>
                        {scheduledMembership.title ===
                        "Membership Cancellation" ? (
                          <div className={styles.premierIcon}>
                            <FontAwesomeIcon
                              icon={faMinusCircle}
                              style={{ color: "lightgrey", fontSize: "30px" }}
                            />
                          </div>
                        ) : (
                          <img
                            src={getMembershipIcon(scheduledMembership.title)}
                            alt="Premier Icon"
                            className={styles.premierIcon}
                          />
                        )}
                        <div className={styles.scheduledChangesMembershipInfo}>
                          {scheduledMembership.title ===
                          "Membership Cancellation" ? (
                            <>{t("Membership Cancellation")}</>
                          ) : (
                            <div className={styles.scheduledChangesHeader}>
                              <span className={styles.scheduledChangesType}>
                                {scheduledMembership.title.includes(
                                  "Premier Membership"
                                )
                                  ? "Premier Membership"
                                  : "Basic Membership"}
                                {(scheduledMembership.title.includes(
                                  "Digital Delivery"
                                ) ||
                                  scheduledMembership.title.includes(
                                    "Topics"
                                  )) &&
                                  " + "}
                                {scheduledMembership.title.includes(
                                  "Digital Delivery"
                                ) &&
                                  !scheduledMembership.title.includes(
                                    "Topics"
                                  ) &&
                                  "Digital Delivery"}
                              </span>
                              <ul className={styles.scheduledChangesList}>
                                {scheduledMembership.title.includes(
                                  "Digital Delivery"
                                ) &&
                                  scheduledMembership.title.includes(
                                    "Topics"
                                  ) && <li>Digital Delivery</li>}
                                {scheduledMembership.title.includes(
                                  "Topics"
                                ) && (
                                  <>
                                    <li>Topics&#8482;</li>
                                  </>
                                )}
                              </ul>
                            </div>
                          )}
                          {scheduledMembership.title !==
                          "Membership Cancellation" ? (
                            <div className={styles.priceBilling}>
                              <span className={styles.price}>
                                ${scheduledMembership.amount.toFixed(2)}
                                {t("/Mo")}
                              </span>
                              <span className={styles.nextBilling}>
                                {t("Next Billing")}{" "}
                                {membershipDates.nextBilledOn}
                              </span>
                            </div>
                          ) : (
                            <div className={styles.startDate}>
                              {t("Effective")}{" "}
                              {formatDateToOne(scheduledMembership.effectiveOn)}
                            </div>
                          )}
                          {scheduledMembership.title !==
                            "Membership Cancellation" && (
                            <span className={styles.startDate}>
                              {t("Starts")}{" "}
                              {formatDateToOne(scheduledMembership.effectiveOn)}
                            </span>
                          )}
                        </div>
                      </div>
                      {/* {headerText !== "Settings" && (
                        <Button
                          buttonText={t("Remove")}
                          onClick={() => handleRemove()}
                          className={styles.removebutton}
                        />
                      )} */}
                    </div>
                  </>
                )}
              <div className={styles.receipt}>
                <h2 className={styles.receiptHeader}>{t("Receipt")}</h2>
                <p className={styles.border}>
                  {t("Upgrade to")} "
                  {receiptData.membership.replace(/\bDreamStream\b/i, "Topics")}
                  ":
                </p>
                <div className={styles.receiptDetails}>
                  <div className={styles.totalPaid}>
                    <div className={styles.totalPaidRow}>
                      <strong>{t("Total Paid")}</strong>
                      <span className={styles.receiptPrice}>
                        {String(receiptData.amountPaid).toLowerCase() !==
                          "free" && "$"}
                        {receiptData.amountPaid}
                      </span>
                    </div>
                  </div>
                  <div className={styles.paymentMethod}>
                    <strong>{t("Payment Method")}</strong>
                    <div className={styles.paymentDetails}>
                      <FontAwesomeIcon
                        icon={getCardIcon(receiptData.cardType)}
                        className={styles.cardIcon}
                      />
                      <span className={styles.paymentInfo}>
                        {t("ending in")} {receiptData.cardLastFour}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div>
              {!hasCurrentMembership && showRenewMessage && (
                <div className={styles.renewMessage}>
                  <div className={styles.iconBox}>
                    <FontAwesomeIcon
                      icon={faExclamationTriangle}
                      className={styles.warningIcon}
                    />
                  </div>
                  {statusMessage && (
                    <div className={styles.statusMessage}>
                      {statusMessage}.&nbsp;
                    </div>
                  )}
                  <span
                    className={styles.renewLink}
                    onClick={handleClickMembershipItem}
                  >
                    {t("Click here to renew")}
                  </span>
                </div>
              )}

              {/* Show Review Your Order section if no billing card */}
              {showNewMembershipSelection &&
                !showReviewOrder &&
                !showAddNewCardButton && (
                  <div>
                    {/* {!showBillingAndResources &&
                    scheduledMembership.title == undefined &&
                    !showReviewOrder &&
                    !hasCurrentMembership &&
                    showNewMembershipSelection && (
                      <> */}
                    <span className={styles.selectedHeader}>
                      {t("Select Your Membership")}
                    </span>
                    {availableMemberships.map((membership, index) => (
                      <div>
                        {membership.title !== "Membership Cancellation" && (
                          <div
                            key={index}
                            className={`${styles.newMemberContainer} ${
                              (
                                availableMemberships[
                                  availableMemberships.length - 1
                                ].title === "Membership Cancellation"
                                  ? index === availableMemberships.length - 2 // Apply style to the second-to-last item if last item is "Membership Cancellation"
                                  : index === availableMemberships.length - 1
                              )
                                ? // Apply style to the last item otherwise
                                  styles.newMemberContainerLastItem
                                : ""
                            }`}
                          >
                            <div className={styles.selectBtnContainer}>
                              {availableMembershipSelected === index &&
                              selected ? (
                                <span
                                  className={styles.checkIcon}
                                  onClick={() => cancelMembership()}
                                >
                                  <img src={checkMark} alt="Check Mark" />
                                </span>
                              ) : (
                                <Button
                                  buttonText={t("Select")}
                                  onClick={() => handleSelectMembership(index)}
                                  className={styles.button}
                                />
                              )}
                            </div>
                            <div className={styles.spacer}>
                              <div className={styles.newMemberIconContainer}>
                                <img
                                  src={getMembershipIcon(membership.title)}
                                  alt="Membership Icon"
                                  className={
                                    membership.title == "Premier Membership" &&
                                    styles.newMemberPremierIcon
                                  }
                                />
                              </div>
                              <div className={styles.membershipInfo}>
                                <span className={styles.membershipType}>
                                  {membership.title}
                                </span>
                                <div className={styles.newMemberPriceContainer}>
                                  <span className={styles.price}>
                                    ${membership.amount.toFixed(2)}
                                    {t("/Mo")}
                                  </span>
                                  <span className={styles.todayPrice}>
                                    {t("Today")}
                                    {calculateProratedAmount(
                                      membership
                                    )?.toFixed(2)}
                                  </span>
                                </div>
                                {availableMembershipSelected === index &&
                                  selected && (
                                    <span className={styles.startDate}>
                                      {t("Starts")} {effectiveDate}
                                    </span>
                                  )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                    {/* </>
                    )} */}
                  </div>
                )}

              {showReviewOrder && (
                <>
                  <span className={styles.selectedHeader}>
                    {t("Review Your Order")}
                  </span>
                  <span className={styles.newMemberText}>
                    {t("New Membership")}
                  </span>

                  {!showBillingAndResources &&
                    scheduledMembership.title === undefined &&
                    availableMemberships.map((membership, index) => (
                      <>
                        {!(selected && availableMembershipSelected !== index) &&
                          membership.title !== "Membership Cancellation" &&
                          membership.title !== currentMembership.type && (
                            <div className={styles.memberContainer} key={index}>
                              <div className={`${styles.spacer}`}>
                                <div className={styles.iconContainer}>
                                  <img
                                    src={getMembershipIcon(membership.title)}
                                    alt="Premier Icon"
                                    className={styles.premierIcon}
                                  />
                                </div>

                                <div className={styles.membershipInfo}>
                                  <span className={styles.membershipType}>
                                    {membership.title}
                                  </span>
                                  <div className={styles.priceContainer}>
                                    <span className={styles.price}>
                                      ${membership.amount.toFixed(2)}
                                      {t("/Mo")}
                                    </span>
                                    <div>
                                      <span className={styles.todayPrice}>
                                        {t("Today:")}
                                        {calculateProratedAmount(
                                          membership
                                        ).toFixed(2)}
                                      </span>
                                    </div>
                                  </div>
                                  {selected && (
                                    <span className={styles.startDate}>
                                      {t("Starts")} {effectiveDate}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div>
                                <Button
                                  buttonText={t("Remove")}
                                  onClick={cancelMembership}
                                  className={styles.button}
                                />
                              </div>
                            </div>
                          )}
                      </>
                    ))}
                  <div
                    className={styles.pointer}
                    onClick={() => handleAddBillingCard()}
                  >
                    <BillingItem
                      info={billingInfo}
                      onClick={handleAddBillingCard}
                    />
                  </div>
                  {!hasAddress && (
                    <div
                      className={styles.pointer}
                      onClick={() => handleAddAddressCard()}
                    >
                      <MembershipAddressItem onClick={handleAddAddressCard} />
                    </div>
                  )}
                </>
              )}

              {/* Membership Item */}
              <div className={styles.membership}>
                {showMembershipAndResources && hasCurrentMembership && (
                  <>
                    <div className={styles.topContainer}>
                      <span className={styles.header}>
                        {t("Current Membership")}
                      </span>
                      <MembershipItem
                        info={currentMembership}
                        // info={null}
                        onClick={handleClickMembershipItem}
                        hideChevron={chevronClicked}
                        changesScheduled={
                          scheduledMembership.title === undefined ? false : true
                        }
                        onMainPage={headerText === "Settings" ? true : false}
                        icon={getMembershipIcon(currentMembership.type)}
                        hasDigitalDelivery={hasDigitalDelivery}
                        hasDreamStream={hasDreamStream}
                        isPremier={isPremier}
                        isFreeTrial={isFreeTrial}
                        compIBOInfo={formatIBODate(compIBONextBillDate)}
                      />
                    </div>
                    {scheduledMembership.title === undefined ? (
                      <>
                        <div className={styles.topContainerDivider} />
                      </>
                    ) : (
                      <div className={styles.scheduledChangesDivider}>
                        {t("Scheduled Changes")}
                      </div>
                    )}
                  </>
                )}
              </div>
              {/* Scheduled Changes Section */}
              {scheduledMembership.title !== undefined &&
                !showAddNewCardButton && (
                  <>
                    <div
                      className={
                        headerText === "Settings"
                          ? styles.scheduledChangesContainer
                          : styles.scheduledChangesContainerInside
                      }
                      style={{
                        cursor:
                          headerText === "Settings" ? "pointer" : "default",
                      }}
                      onClick={() => handleClickMembershipItem()}
                    >
                      <div className={styles.spacer}>
                        {scheduledMembership.title ===
                        "Membership Cancellation" ? (
                          <div className={styles.premierIcon}>
                            <FontAwesomeIcon
                              icon={faMinusCircle}
                              style={{ color: "lightgrey", fontSize: "30px" }}
                            />
                          </div>
                        ) : (
                          <img
                            src={getMembershipIcon(scheduledMembership.title)}
                            alt="Premier Icon"
                            className={styles.premierIcon}
                          />
                        )}
                        <div className={styles.scheduledChangesMembershipInfo}>
                          {scheduledMembership.title ===
                          "Membership Cancellation" ? (
                            <>{t("Membership Cancellation")}</>
                          ) : (
                            <div className={styles.scheduledChangesHeader}>
                              <span className={styles.scheduledChangesType}>
                                {scheduledMembership.title.includes(
                                  "Premier Membership"
                                )
                                  ? "Premier Membership"
                                  : "Basic Membership"}
                                {(scheduledMembership.title.includes(
                                  "Digital Delivery"
                                ) ||
                                  scheduledMembership.title.includes(
                                    "Topics"
                                  )) &&
                                  " + "}
                                {scheduledMembership.title.includes(
                                  "Digital Delivery"
                                ) &&
                                  !scheduledMembership.title.includes(
                                    "Topics"
                                  ) &&
                                  "Digital Delivery"}
                              </span>
                              <ul className={styles.scheduledChangesList}>
                                {scheduledMembership.title.includes(
                                  "Digital Delivery"
                                ) &&
                                  scheduledMembership.title.includes(
                                    "Topics"
                                  ) && <li>Digital Delivery</li>}
                                {scheduledMembership.title.includes(
                                  "Topics"
                                ) && (
                                  <>
                                    <li>Topics&#8482;</li>
                                  </>
                                )}
                              </ul>
                            </div>
                          )}
                          {scheduledMembership.title !==
                          "Membership Cancellation" ? (
                            <div className={styles.priceBilling}>
                              <span className={styles.price}>
                                ${scheduledMembership.amount.toFixed(2)}
                                {t("/Mo")}
                              </span>
                              <span className={styles.nextBilling}>
                                {t("Next Billing")}{" "}
                                {membershipDates.nextBilledOn}
                              </span>
                            </div>
                          ) : (
                            <div className={styles.startDate}>
                              {t("Effective")}{" "}
                              {formatDateToOne(scheduledMembership.effectiveOn)}
                            </div>
                          )}
                          {scheduledMembership.title !==
                            "Membership Cancellation" && (
                            <span className={styles.startDate}>
                              {t("Starts")}{" "}
                              {formatDateToOne(scheduledMembership.effectiveOn)}
                            </span>
                          )}
                        </div>
                      </div>
                      {headerText !== "Settings" && (
                        <Button
                          buttonText={t("Remove")}
                          onClick={() => handleRemove()}
                          className={styles.removebutton}
                        />
                      )}
                    </div>
                  </>
                )}
              {/* Billing and Card Management */}
              <div>
                {showAddNewCardButton ? (
                  // Card Management Section
                  <>
                    <div className={styles.billingHeader}>
                      {t("Primary Billing Payment Method")}
                    </div>
                    <div className={styles.addNewCardButton}>
                      <Button
                        buttonText={t("Add Card")}
                        onClick={openCardModal}
                      />

                      <CardModal
                        isOpen={isCardModalOpen}
                        onClose={() => setIsCardModalOpen(false)}
                        card={currentCard}
                        onAdd={handleAdd}
                        onValidate={() => {}}
                        isEditing={false}
                        onTokenReceived={handleTokenReceived}
                        isLoading={isModalLoading}
                      />
                    </div>
                    <div className={styles.cardList}>
                      {/* List of Cards */}
                      <div className={styles.cardList}>
                        {cardList.map((card, index) => {
                          const isNotExpired = checkCardExpiration(
                            card.expiration
                          );

                          return (
                            <div
                              className={`${styles.cardItem} ${
                                card.selected ? styles.selectedCardItem : ""
                              } ${!isNotExpired ? styles.cardExpired : ""}`}
                              key={index}
                            >
                              {card.selected ? (
                                <div className={styles.checkIcon}>
                                  <img src={cardCheck} alt="Check Mark" />
                                </div>
                              ) : (
                                <Button
                                  buttonText={t("Select")}
                                  onClick={() => handleCardSelection(index)}
                                  className={styles.cardSelect}
                                  disabled={!isNotExpired}
                                />
                              )}
                              <FontAwesomeIcon
                                icon={getCardIcon(card.cardType)}
                                aria-hidden="true"
                                className={styles.cardIcon}
                              />
                              <div className={styles.cardDetails}>
                                <div className={styles.inline}>
                                  <span className={styles.lastFour}>
                                    {t("Ending in")} {card.lastFour}
                                  </span>
                                  <span className={styles.cardExpiration}>
                                    Exp: {card.expiration}
                                  </span>
                                </div>
                                <span className={styles.name}>{card.name}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    {showBillingAndResources && (
                      <>
                        <div
                          className={styles.pointer}
                          onClick={() => handleClickBillingItem()}
                        >
                          <BillingItem
                            info={billingInfo}
                            onClick={handleClickBillingItem}
                          />
                        </div>
                        {/* <div className={styles.resources}>
                          <ResourceList resources={resources} />
                        </div> */}
                        <div className={styles.resources}>
                          <ResourceList resources={resourcesData} />
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
              {/* Membership Selection */}
              {!showBillingAndResources &&
                scheduledMembership.title === undefined &&
                hasCurrentMembership &&
                // displayPremier &&
                // isPremier &&
                !showReviewOrder && (
                  <>
                    {/* {!(selected && availableMembershipSelected !== index) &&
                      membership.title !== "Membership Cancellation" &&
                      membership.title !== currentMembership.type && ( */}
                    <div className={styles.buttonAndInfoContainer}>
                      {/* Premier Membership */}
                      <div
                        className={`${styles.spacer} ${styles.selectOption}`}
                      >
                        <div className={styles.checkBox}>
                          {isPremier ? (
                            <img
                              alt="Check Mark"
                              src={checkMark}
                              className={styles.checkBoxMark}
                            />
                          ) : (
                            <div>
                              <Checkbox
                                isChecked={changedStates.premier}
                                onChange={() => {
                                  handleCheckBoxChange("PM");
                                  setShowConfirmationBox(false);
                                }}
                              />
                            </div>
                          )}
                        </div>
                        <div className={styles.iconContainer}>
                          <img
                            src={getMembershipIcon(currentMembership.type)}
                            alt="Premier Icon"
                            className={styles.premierIcon}
                          />
                        </div>

                        <div className={styles.infoContainer}>
                          <div className={styles.membershipInfo}>
                            <span className={styles.membershipType}>
                              {t("Premier Membership")}
                            </span>
                            <div className={styles.priceContainer}>
                              <span className={styles.price}>
                                $
                                {isFreeTrial
                                  ? "0.00"
                                  : availableMemberships
                                      .find(
                                        (membership) =>
                                          membership.title ==
                                          "Premier Membership"
                                      )
                                      .amount.toFixed(2)}
                                {t("/Mo")}
                              </span>
                              {isFreeTrial && (
                                <div>
                                  <span className={styles.normallyPrice}>
                                    {t("Normally")}
                                    {availableMemberships
                                      .find(
                                        (membership) =>
                                          membership.title ==
                                          "Premier Membership"
                                      )
                                      .amount.toFixed(2)}
                                    {t("/Mo")}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                          <img
                            alt="Info Icon"
                            src={infoIcon}
                            onClick={() => setShowInfoModal("PM")}
                            className={styles.infoButton}
                          />
                        </div>
                      </div>

                      {/* Digital Delivery */}
                      {displayDigitalDelivery && (
                        <div
                          className={`${styles.spacer} ${styles.selectOption} `}
                        >
                          <div className={styles.checkBox}>
                            {isFreeTrial ? (
                              <img
                                alt="Check Mark"
                                src={checkMark}
                                className={styles.checkBoxMark}
                              />
                            ) : (
                              <div>
                                <Checkbox
                                  isChecked={changedStates.digitalDelivery}
                                  onChange={() => {
                                    handleCheckBoxChange("DD");
                                    setShowConfirmationBox(false);
                                  }}
                                />
                              </div>
                            )}
                          </div>
                          <div className={styles.iconContainer}>
                            <img
                              src={digitalDeliveryIcon}
                              alt="Digital Delivery Icon"
                              className={styles.digitalDeliveryIcon}
                            />
                          </div>

                          <div className={styles.infoContainer}>
                            <div className={styles.membershipInfo}>
                              <span className={styles.membershipType}>
                                Digital Delivery
                              </span>
                              <div className={styles.priceContainer}>
                                <span className={styles.price}>
                                  $
                                  {isFreeTrial
                                    ? "0.00"
                                    : (
                                        availableMemberships.find(
                                          (membership) =>
                                            membership.title ==
                                            "Premier Membership + Digital Delivery"
                                        ).amount -
                                        availableMemberships.find(
                                          (membership) =>
                                            membership.title ==
                                            "Premier Membership"
                                        ).amount
                                      ).toFixed(2)}
                                  {t("/Mo")}
                                </span>
                                {isFreeTrial && (
                                  <div>
                                    <span className={styles.normallyPrice}>
                                      {t("Normally")}
                                      {(
                                        availableMemberships.find(
                                          (membership) =>
                                            membership.title ==
                                            "Premier Membership + Digital Delivery"
                                        ).amount -
                                        availableMemberships.find(
                                          (membership) =>
                                            membership.title ==
                                            "Premier Membership"
                                        ).amount
                                      ).toFixed(2)}
                                      {t("/Mo")}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                            <img
                              alt="Info Icon"
                              src={infoIcon}
                              onClick={() => setShowInfoModal("DD")}
                              className={styles.infoButton}
                            />
                          </div>
                        </div>
                      )}

                      {/* DreamStream + Topics */}
                      {displayDreamStream && (
                        <>
                          {/* DreamStream */}
                          {/*
                          <div
                            className={`${styles.spacer} ${styles.selectOption}`}
                          >
                            <div className={styles.checkBox}>
                              {isFreeTrial ? (
                                <img
                                  alt="Check Mark"
                                  src={checkMark}
                                  className={styles.checkBoxMark}
                                />
                              ) : (
                                <Checkbox
                                  isChecked={changedStates.dreamStream}
                                  onChange={() => handleCheckBoxChange("DS")}
                                />
                              )}
                            </div>
                            {/* Dreamstream Icon */}
                          {/*
                            <div className={styles.dreamStreamIconContainer}>
                              <img
                                src={dreamStreamLogoBackground}
                                alt="DreamStream Icon Background"
                                className={styles.dreamStreamBackground}
                              />
                              <img
                                src={dreamStreamLogoSymbol}
                                alt="DreamStream Icon Symbol"
                                className={styles.dreamStreamSymbol}
                              />
                            </div>

                            <div className={styles.infoContainer}>
                              <div className={styles.membershipInfo}>
                                <span className={styles.membershipType}>
                                  DreamStream&#174;
                                </span>
                                <div className={styles.priceContainer}>
                                  <span className={styles.price}>
                                    $
                                    {isFreeTrial
                                      ? "0.00"
                                      : (
                                          availableMemberships.find(
                                            (membership) =>
                                              membership.title ==
                                              "Premier Membership + DreamStream"
                                          ).amount -
                                          availableMemberships.find(
                                            (membership) =>
                                              membership.title ==
                                              "Premier Membership"
                                          ).amount
                                        ).toFixed(2)}
                                    {t("/Mo")}
                                  </span>
                                  {isFreeTrial && (
                                    <div>
                                      <span className={styles.normallyPrice}>
                                        {t("Normally:")}
                                        {(
                                          availableMemberships.find(
                                            (membership) =>
                                              membership.title ==
                                              // "Premier Membership + DreamStream"
                                              "Premier Membership + Digital Delivery + Topics"
                                          ).amount -
                                          availableMemberships.find(
                                            (membership) =>
                                              membership.title ==
                                              "Premier Membership"
                                          ).amount
                                        ).toFixed(2)}
                                        {t("/Mo")}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <img
                                alt="Info Icon"
                                src={infoIcon}
                                onClick={() => setShowInfoModal("DS")}
                                className={styles.infoButton}
                              />
                            </div>
                          </div>
                          */}

                          {/* Topics */}
                          <div
                            className={`${styles.spacer} ${styles.selectOption}`}
                          >
                            <div className={styles.checkBox}>
                              {isFreeTrial && hasDreamStream ? (
                                <img
                                  alt="Check Mark"
                                  src={checkMark}
                                  className={styles.checkBoxMark}
                                />
                              ) : (
                                <div>
                                  <Checkbox
                                    isChecked={changedStates.dreamStream}
                                    onChange={() => {
                                      handleCheckBoxChange("DS");
                                      setShowConfirmationBox(false);
                                    }}
                                  />
                                </div>
                              )}
                            </div>
                            <div className={styles.dreamStreamIconContainer}>
                              <img
                                src={topicsLogoBackground}
                                alt="Topics Icon Background"
                                className={styles.topicsBackground}
                              />
                              <img
                                src={topicsLogoSymbol}
                                alt="Topics Icon Symbol"
                                className={styles.topicsSymbol}
                              />
                            </div>
                            <div className={styles.infoContainer}>
                              <div className={styles.membershipInfo}>
                                <span className={styles.membershipType}>
                                  Topics&#8482;
                                </span>
                                <div className={styles.priceContainer}>
                                  <span className={styles.price}>
                                    $
                                    {isFreeTrial
                                      ? "0.00"
                                      : (
                                          availableMemberships.find(
                                            (membership) =>
                                              membership.title ==
                                              "Premier Membership + Topics"
                                          ).amount -
                                          availableMemberships.find(
                                            (membership) =>
                                              membership.title ==
                                              "Premier Membership"
                                          ).amount
                                        ).toFixed(2)}
                                    {t("/Mo")}
                                  </span>
                                  {isFreeTrial && (
                                    <div>
                                      <span className={styles.normallyPrice}>
                                        {t("Normally")}
                                        {(
                                          availableMemberships.find(
                                            (membership) =>
                                              membership.title ==
                                              "Premier Membership + Topics"
                                          ).amount -
                                          availableMemberships.find(
                                            (membership) =>
                                              membership.title ==
                                              "Premier Membership"
                                          ).amount
                                        ).toFixed(2)}
                                        {t("/Mo")}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <img
                                alt="Info Icon"
                                src={infoIcon}
                                onClick={() => setShowInfoModal("DS")}
                                className={styles.infoButton}
                              />
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                    {/* )} */}
                  </>
                )}
              {/* Cancel Membership */}
              <div className={styles.cancelContainer}>
                {hasCurrentMembership &&
                  !showAddNewCardButton &&
                  // displayPremier &&
                  // isPremier &&
                  changedStates.digitalDelivery ==
                    initialStates.digitalDelivery &&
                  changedStates.dreamStream == initialStates.dreamStream &&
                  changedStates.premier == initialStates.premier &&
                  scheduledMembership.title !== "Membership Cancellation" &&
                  headerText === "Membership" && (
                    <div className={styles.cancelButtonContainer}>
                      <div className={styles.cancel} onClick={cancelOnClick}>
                        {t("Cancel My Membership")}
                      </div>
                    </div>
                  )}
                {(changedStates.digitalDelivery !==
                  initialStates.digitalDelivery ||
                  changedStates.dreamStream !== initialStates.dreamStream ||
                  changedStates.premier !== initialStates.premier) &&
                  !showAddNewCardButton &&
                  !showProcessConfirmationBox &&
                  // isPremier &&
                  // displayPremier &&
                  headerText === "Membership" && (
                    <>
                      {!isRefetching ? (
                        <div
                          className={styles.submitChangesBtn}
                          disabled={isLoading}
                          onClick={() => !isLoading && handleSelect()}
                        >
                          {t("Submit Changes")}
                        </div>
                      ) : (
                        <div className={styles.buttonLoaderContainer}>
                          <Loader color={"#415364"} size={42} />
                        </div>
                      )}
                    </>
                  )}
              </div>
              {/* Cancel Membership Modal */}
              {showCancelModal && (
                <div className={styles.modalOverlay}>
                  <div className={styles.modalContent}>
                    <img
                      alt="Premier Icon Light"
                      src={PremierLight}
                      className={styles.premierIconLight}
                    />
                    <div className={styles.modalTitle}>{t("Are You Sure")}</div>
                    <div className={styles.modalBody}>
                      {t("Are you sure you want to cancel")}
                    </div>
                    <div className={styles.modalDate}>
                      {t("Effective Date")} {currentMembership.nextBilling}
                    </div>
                    <div className={styles.modalButtons}>
                      <button
                        className={styles.noBtn}
                        onClick={cancelMembershipModal}
                      >
                        {t("No")}
                      </button>
                      <button
                        className={styles.yesBtn}
                        onClick={confirmCancelMembershipModal}
                      >
                        {t("Yes")}
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {showInfoModal == "PM" && (
                <div className={styles.modalOverlay}>
                  <div className={styles.modalContentPMInfo}>
                    <div className={styles.modalContentPMInner}>
                      <div className={styles.modalHeader}>
                        <img
                          alt="Premier Icon Dark"
                          src={PremierDark}
                          className={styles.premierIconDark}
                        />
                        <img
                          alt="Close Button"
                          src={InfoCloseBtn}
                          onClick={() => setShowInfoModal("NA")}
                          className={styles.infoModalCloseBtn}
                        />
                      </div>
                      <div className={styles.modalTitlePM}>
                        {t("Premier Membership")}
                      </div>
                      <div className={styles.modalBodyPM}>
                        {t("The baseline for a business builder")}
                      </div>
                      <div className={styles.membershipDetailsHeaderPM}>
                        {isANZ
                          ? "Mobile app and wwdb.com.au"
                          : t("Mobile app and wwdb.com")}
                      </div>
                      <div className={styles.detailsListPM}>
                        <div>
                          {t("Access to our world-class app and website")}
                        </div>
                        <ul>
                          <li>WWG News</li>
                          {isANZ && <li>Events</li>}
                          {!isANZ && <li>WWG Store*</li>}
                          <li>Digital Delivery**</li>
                          <li>Calendar</li>
                          <li>Digital Info</li>
                          <li>Retail</li>
                          <li>Info Center</li>
                          <li>Contacts</li>
                          <li>My Business</li>
                          {!isANZ && <li>Edibles</li>}
                        </ul>
                      </div>
                      <div className={styles.discountInfoPM}>
                        <p>{t("DreamStream®")}</p>
                      </div>
                      <div className={styles.modalBodyPM}>
                        {t(
                          "Private social media platform where you can share and view motivational and business-building content"
                        )}
                        .{/* TODO: Needs translation */}
                      </div>
                      {!isANZ && (
                        <div className={styles.discountInfoPM}>
                          <p>{t("Discounts on All BSM")}</p>
                        </div>
                      )}
                      <div className={styles.discountInfoPM}>
                        <p>{t("Access to Live Help")}</p>
                        {/* TODO: Needs translation */}
                      </div>
                      <div className={styles.modalBodyPM}>
                        *{t("2nd year IBOs & beyond")}
                        <br />
                        **
                        {t(
                          "Digital Delivery is an additional $29.99/mo for 2nd year IBOs & beyond"
                        )}
                        .{/* TODO: Needs translation */}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {showInfoModal == "DD" && (
                <div className={styles.modalOverlay}>
                  <div className={styles.modalContentPM}>
                    <div className={styles.modalHeader}>
                      <img
                        alt="Digital Delivery Icon Dark"
                        src={DigitalDeliveryDark}
                        className={styles.premierIconDark}
                      />
                      <img
                        alt="Close Button"
                        src={InfoCloseBtn}
                        onClick={() => setShowInfoModal("NA")}
                        className={styles.infoModalCloseBtn}
                      />
                    </div>
                    <div className={styles.modalTitlePM}>Digital Delivery</div>
                    <div className={styles.modalBodyPM}>
                      {t("Enjoy MP3 and MP4 audio spanning")}
                    </div>
                    <div className={styles.detailsListPM}>
                      <div className={styles.detailsHeaderPM}>
                        {t("For an additional")}
                        {isFreeTrial
                          ? // ? "0.00"
                            "29.99"
                          : (
                              availableMemberships.find(
                                (membership) =>
                                  membership.title ==
                                  "Premier Membership + Digital Delivery"
                              ).amount -
                              availableMemberships.find(
                                (membership) =>
                                  membership.title == "Premier Membership"
                              ).amount
                            ).toFixed(2)}{" "}
                        {t("per month")}
                      </div>
                      <ul>
                        <li>{t("New Digital Delivery")}</li>
                        <li>{t("Access to hundreds of other audios")}</li>
                        {!isANZ && <li>{t("Free Shipping on one BSM")}</li>}
                      </ul>
                    </div>
                    <div className={styles.discountInfoDD}>
                      <p className={styles.secondYrText}>
                        {t("2nd year IBOs and beyond")}
                      </p>
                      <p>{t("Digital Delivery is included")}</p>
                    </div>
                  </div>
                </div>
              )}
              {showInfoModal == "DS" && (
                <div className={styles.modalOverlay}>
                  <div className={styles.modalContentPM}>
                    <div className={styles.modalHeader}>
                      <div className={styles.dreamStreamTopicsIconsContainer}>
                        {/* <img
                          alt="DreamStream Icon Dark"
                          src={DreamStreamDark}
                          className={styles.dreamStreamIconDark}
                        /> */}
                        <div className={styles.dreamStreamIconContainerDark}>
                          <img
                            src={topicsLogoBackgroundDark}
                            alt="Topics Icon Background Dark"
                            className={styles.topicsBackgroundDark}
                          />
                          <img
                            src={topicsLogoSymbol}
                            alt="Topics Icon Symbol"
                            className={styles.topicsSymbol}
                          />
                        </div>
                      </div>
                      <img
                        alt="Close Button"
                        src={InfoCloseBtn}
                        onClick={() => setShowInfoModal("NA")}
                        className={styles.infoModalCloseBtn}
                      />
                    </div>
                    <div className={styles.modalTitlePM}>
                      {/* <div>DreamStream® </div> */}
                      <div>Topics™</div>
                    </div>
                    <div className={styles.modalBodyPM}>
                      <p> {t("Customized communication tool")}</p>
                      {t("For an additional")}
                      {isANZ
                        ? "29.99"
                        : isFreeTrial
                        ? // ? "0.00"
                          "29.99"
                        : (
                            availableMemberships.find(
                              (membership) =>
                                membership.title ==
                                "Premier Membership + Topics"
                            ).amount -
                            availableMemberships.find(
                              (membership) =>
                                membership.title == "Premier Membership"
                            ).amount
                          ).toFixed(2)}{" "}
                      {t("per month our DreamStream and Topics")}
                    </div>
                    {/* <div className={styles.modalSubTitlePM}>DreamStream</div> */}
                    {/* <div className={styles.modalBodyPM}>
                      {t("Private social media platform")}
                    </div> */}
                    {/* <div className={styles.modalSubTitlePM}>Topics</div>
                    <div className={styles.modalBodyPM}>
                      {t("Customized communication tool")}
                    </div> */}
                    <div className={styles.discountInfoDD}>
                      {/* <div>{t("2nd year IBOs and beyond")}</div> */}
                      <p className={styles.secondYrText}>
                        {t("2nd year IBOs and beyond")}
                      </p>
                      <p>{t("Topics is included")}</p>
                    </div>
                  </div>
                </div>
              )}
              <Modal isOpen={showRemoveModal}>
                <div className={styles.modal}>
                  <div className={styles.modalTitle}>{t("Are you sure")}</div>
                  <div className={styles.removeModalBody}>
                    {t("Are you sure you want to remove")}
                    {scheduledMembership.title !== "Membership Cancellation"
                      ? scheduledMembership.title
                      : "Cancel Your Membership"}
                    "?
                  </div>

                  <div className={styles.removeModalButton}>
                    <Button
                      buttonText={t("Yes")}
                      onClick={handleRemoveConfirm}
                    />
                    <Button buttonText={t("No")} onClick={handleRemoveCancel} />
                  </div>
                </div>
              </Modal>
              <Modal isOpen={showNoticeModal}>
                <div className={styles.modal}>
                  <div className={styles.modalTitle}>
                    {t("Before You Cancel")}
                  </div>
                  <div className={styles.removeModalBody}>
                    {t("You Must Remove your request")}
                    {scheduledMembership.title}
                    {t("before you can cancel your membership")}
                    {/* {scheduledMembership.title !== "Membership Cancellation"
                      ? scheduledMembership.title
                      : "Cancel Your Membership"}
                    "? */}
                  </div>

                  <div className={styles.removeModalButton}>
                    <Button
                      buttonText={t("Close")}
                      onClick={handleCloseNoticeModal}
                    />
                  </div>
                </div>
              </Modal>
              <Modal isOpen={showTOSModal}>
                <TermsAndConditions
                  onBack={handleTaCCancel}
                  onYes={handleTaCConfirm}
                />
              </Modal>
              <WarningPrimaryModal
                isOpen={showPremierWarningModal}
                title={"Premier Membership Needed"}
                description={t("You must upgrade to a Premier")}
                onClick={() => setShowPremierWarningModal(false)}
              />
              {/* Confirmation Box */}
              {showConfirmationBox && (
                <div className={styles.confirmationBox}>
                  <div className={styles.topLine}>
                    <div className={styles.title}>
                      {availableMemberships[availableMembershipSelected].title}:
                    </div>
                    <div className={styles.priceLine}>
                      {(() => {
                        const { price, tax } = calculateFooterPriceAndTax(
                          availableMemberships[availableMembershipSelected]
                        );
                        return (
                          <span>
                            {console.log("test", price)}
                            {String(price).toLowerCase() !== "free" && "$"}
                            {String(price).toLowerCase() !== "free"
                              ? parseFloat(price)?.toFixed(2)
                              : "Free"}
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                  {(() => {
                    const { price, tax } = calculateFooterPriceAndTax(
                      availableMemberships[availableMembershipSelected]
                    );

                    if (
                      String(price).toLowerCase() !== "free" &&
                      parseFloat(price) !== 0
                    ) {
                      return (
                        <>
                          <div className={styles.taxLine}>
                            <div className={styles.title}>{t("Tax")}</div>
                            <div>${tax}</div>
                          </div>
                          <div className={styles.taxLine}>
                            {" "}
                            {/* Reusing the taxLine styles for consistency */}
                            <div className={styles.title}>{t("Total")}</div>
                            <div>
                              {String(price).toLowerCase() !== "free" && "$"}
                              {(parseFloat(price) + parseFloat(tax))?.toFixed(
                                2
                              )}
                            </div>{" "}
                            {/* Calculating total */}
                          </div>
                        </>
                      );
                    }

                    return null; // Don't render the tax or total line if it's free or 0.00
                  })()}
                  <div className={styles.bottomLine}>
                    <div className={styles.title}>{t("Effective Date")}</div>
                    <div>{effectiveDate}</div>
                  </div>
                  <div className={styles.buttons}>
                    <Button
                      buttonText={t("Cancel")}
                      onClick={cancelMembership}
                      className={styles.cancelButton}
                    />
                    <span className={styles.buttonGap} />
                    <Button
                      buttonText={t("Confirm")}
                      onClick={
                        !billingInfo && showReviewOrder
                          ? () => {}
                          : confirmOnClick
                      }
                      className={
                        !billingInfo && showReviewOrder
                          ? styles.disabled
                          : styles.confirmButton
                      }
                      disabled={!billingInfo && showReviewOrder}
                    />
                  </div>
                </div>
              )}
              {/* Process Confirmation Box */}
              {showProcessConfirmationBox && (
                <div className={styles.processConfirmationBox}>
                  <div className={styles.leftSideProcess}>
                    <div className={styles.topLineProcess}>
                      <div className={styles.title}>
                        {
                          availableMemberships[availableMembershipSelected]
                            .title
                        }
                        :
                      </div>
                      <div className={styles.priceLine}>
                        {(() => {
                          const { price } = calculateFooterPriceAndTax(
                            availableMemberships[availableMembershipSelected]
                          );
                          return (
                            <span>
                              {String(price).toLowerCase() !== "free" && "$"}
                              {price}
                            </span>
                          );
                        })()}
                      </div>
                    </div>
                    {(() => {
                      const { price, tax } = calculateFooterPriceAndTax(
                        availableMemberships[availableMembershipSelected]
                      );

                      return (
                        <>
                          <div className={styles.taxLineProcess}>
                            <div className={styles.title}>{t("Tax")}</div>
                            <div>
                              {
                                !hasAddress ? (
                                  "To Be Determined"
                                ) : (
                                  <>
                                    {price == "Free" ? (
                                      "Free"
                                    ) : (
                                      <>
                                        {String(tax).toLowerCase() !== "free" &&
                                          "$"}
                                        {parseFloat(tax)?.toFixed(2)}
                                      </>
                                    )}
                                  </>
                                )
                                // `$${tax}`
                              }
                            </div>
                          </div>
                          <div className={styles.taxLineProcess}>
                            {" "}
                            {/* Reusing the taxLine styles for consistency */}
                            <div className={styles.title}>{t("Total")}</div>
                            <div>
                              {price == "Free" ? (
                                "Free"
                              ) : (
                                <>
                                  {String(price).toLowerCase() !== "free" &&
                                    "$"}
                                  {(
                                    parseFloat(price) + parseFloat(tax)
                                  )?.toFixed(2)}
                                </>
                              )}
                            </div>{" "}
                            {/* Calculating total */}
                          </div>
                        </>
                      );
                    })()}
                    <div className={styles.bottomLineProcess}>
                      <div className={styles.title}>{t("Effective Date")}</div>
                      <div>{effectiveDate}</div>
                    </div>
                  </div>
                  <div className={styles.buttonsProcess}>
                    <Button
                      buttonText={t("Process")}
                      onClick={() => handleProcessClick()}
                      className={
                        !hasAddress ? styles.disabled : styles.confirmButton
                      }
                      disabled={!hasAddress}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
          {showSuccessMessage && (
            <div
              className={`${styles.successMessage} ${styles.successMessageAnimation}`}
            >
              <p>{t("Primary Card Updated")}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default MembershipPage;
